import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AlertCircle, BellRing, CheckCircle2, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTelegramLink } from '../hooks/useTelegramLink';
import { TelegramLinkPanel } from './TelegramLinkPanel';
import { createTracking, previewTracking, TrackerEntry, TrackingPreview, TrackingTarget, trackingErrorKey } from '../api/tracker';
import { analytics } from '../analytics';

interface Props {
  open: boolean;
  onClose: () => void;
  numberAnno?: string;
  lotId?: number;
  onTracked?: (entry: TrackerEntry) => void;
}

export default function TrackProcurementDialog({ open, ...props }: Props) {
  return open ? <TrackingDialog key={`${props.lotId ?? ''}:${props.numberAnno ?? ''}`} {...props} /> : null;
}

function TrackingDialog({ onClose, numberAnno, lotId, onTracked }: Omit<Props, 'open'>) {
  const { t, language } = useLanguage();
  const { user, refreshUser } = useAuth();
  const [number, setNumber] = useState(numberAnno || '');
  const [preview, setPreview] = useState<TrackingPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);
  const titleId = useId();
  const inputId = useId();
  const box = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const request = useRef<AbortController | null>(null);
  const target = useRef<TrackingTarget | null>(null);
  const manual = !lotId && !numberAnno;
  const telegram = useTelegramLink();

  useEffect(() => {
    mounted.current = true;
    const focused = document.activeElement as HTMLElement | null;
    const main = document.getElementById('main-scroll');
    const bodyOverflow = document.body.style.overflow;
    const mainOverflow = main?.style.overflow;
    document.body.style.overflow = 'hidden';
    if (main) main.style.overflow = 'hidden';
    box.current?.focus();
    return () => {
      mounted.current = false;
      request.current?.abort();
      document.body.style.overflow = bodyOverflow;
      if (main) main.style.overflow = mainOverflow || '';
      if (focused?.isConnected) focused.focus();
    };
  }, []);

  const inspect = useCallback(async (value: TrackingTarget) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    target.current = value;
    setLoading(true); setError(''); setPreview(null);
    try {
      const result = await previewTracking(value, controller.signal);
      if (mounted.current && !controller.signal.aborted) setPreview(result);
    } catch (failure) {
      if (mounted.current && !controller.signal.aborted) setError(trackingErrorKey(failure));
    } finally {
      if (mounted.current && !controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (lotId || numberAnno)) void inspect(lotId ? { lot_id: lotId } : { number_anno: numberAnno });
    // Identity comes from the opened target; profile refresh must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotId, numberAnno, inspect, !!user]);

  useEffect(() => {
    if (telegram.step === 'success' && target.current) void inspect(target.current);
  }, [telegram.step, inspect]);

  const confirm = async () => {
    if (submitting.current || !preview || preview.tracked || !preview.telegram_linked || preview.balance < preview.cost || !target.current) return;
    submitting.current = true; setSaving(true); setError('');
    try {
      const entry = await createTracking(target.current);
      analytics.trackingAdded(lotId ? 'lot' : numberAnno ? 'announcement' : 'manual');
      if (mounted.current) {
        setAdded(true);
        setPreview((previous) => previous ? { ...previous, tracked: true, entry_id: entry.id } : previous);
        // Parent refresh is independent of a completed paid activation.
        try { onTracked?.(entry); } catch { /* Confirmation remains successful. */ }
      }
      // Creation is already confirmed. A profile refresh failure must not turn
      // it into an apparent paid-action failure or offer another charge.
      await refreshUser().catch(() => undefined);
    } catch (failure) {
      if (mounted.current) {
        setError(trackingErrorKey(failure));
        setPreview(null); // Recheck server state before another paid attempt.
      }
    } finally {
      submitting.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const keyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && !saving) { event.stopPropagation(); onClose(); }
    if (event.key !== 'Tab') return;
    const elements = box.current?.querySelectorAll<HTMLElement>('input:not(:disabled),button:not(:disabled),a[href]');
    if (!elements?.length) return;
    const first = elements[0], last = elements[elements.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === box.current)) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  return createPortal(<div className="fixed inset-0 z-[220] flex items-center justify-center bg-slate-950/45 p-3 backdrop-blur-sm sm:p-6">
    <div ref={box} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={keyDown}
      className="flex max-h-[calc(100vh-1.5rem)] supports-[height:100dvh]:max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none">
      <header className="flex shrink-0 items-center gap-3 border-b border-slate-100 px-4 py-4 sm:px-6">
        <BellRing aria-hidden="true" className="h-5 w-5 shrink-0 text-goszakup-600" />
        <h2 id={titleId} className="min-w-0 flex-1 text-lg font-bold text-slate-900">{t('tracking.title')}</h2>
        <button type="button" aria-label={t('tracking.close')} disabled={saving} onClick={onClose} className="procurement-button h-11 w-11 shrink-0 !px-0"><X aria-hidden="true" className="h-5 w-5" /></button>
      </header>
      <div className="min-h-0 space-y-4 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6" aria-busy={loading || saving}>
        {!user ? <div className="space-y-3"><p>{t('tracking.loginRequired')}</p><Link to="/login" className="procurement-button procurement-button-primary">{t('tracking.login')}</Link></div> : <>
          <p className="text-sm leading-relaxed text-slate-600">{t('tracking.description')}</p>
          {manual && !preview && <form onSubmit={(event) => { event.preventDefault(); if (number.trim() && !loading) void inspect({ number_anno: number.trim() }); }} className="space-y-3">
            <label htmlFor={inputId} className="block text-sm font-semibold text-slate-800">{t('tracking.number')}</label>
            <input id={inputId} value={number} onChange={(event) => { setNumber(event.target.value); setError(''); }} maxLength={80} disabled={loading || saving} placeholder={t('tracking.placeholder')} className="procurement-input w-full" required />
            <button type="submit" disabled={loading || saving || !number.trim()} className="procurement-button">{t('tracking.review')}</button>
          </form>}
          {loading && <p role="status" className="flex items-center gap-2 text-sm text-slate-500"><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />{t('tracking.checking')}</p>}
          {preview && <>
            <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs text-slate-500">{t('tracking.number')}</p>
              <p className="mt-1 break-words font-bold text-slate-900">{preview.number_anno}</p>
              <p className="mt-2 text-sm text-slate-700 [overflow-wrap:anywhere]">{language === 'kk' ? preview.name_kz || preview.name_ru : preview.name_ru || preview.name_kz}</p>
              <p className="mt-3 text-xs leading-relaxed text-slate-500">{t('tracking.scope')}</p>
            </section>
            {preview.tracked ? <p role="status" className="flex gap-2 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800"><CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0" />{t(added ? 'tracking.added' : 'tracking.alreadyTracked')}</p> : <>
              <section className="space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
                <h3 className="font-bold">{t('tracking.confirmation')}</h3>
                <p>{t('tracking.charge')}</p>
                <p className="text-xs leading-relaxed">{t('tracking.noRefund')}</p>
                <p className="font-semibold">{t('tracking.balance').replace('{count}', String(preview.balance))}</p>
              </section>
              {preview.balance < preview.cost && <div className="space-y-2"><p className="text-sm text-red-700">{t('tracking.noBalance')}</p><Link to="/plans" className="procurement-button">{t('tracking.plans')}</Link></div>}
              {!preview.telegram_linked && <section className="space-y-3 rounded-xl border border-sky-100 p-4"><p className="text-sm text-slate-600">{t('tracking.telegramRequired')}</p><TelegramLinkPanel step={telegram.step} linkData={telegram.linkData} onStart={telegram.startLink} onRetry={telegram.startLink} /></section>}
            </>}
          </>}
          {error && <div role="alert" className="flex gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700"><AlertCircle aria-hidden="true" className="h-5 w-5 shrink-0" /><p>{t(error)}</p></div>}
          {error && !loading && target.current && <button type="button" onClick={() => target.current && void inspect(target.current)} className="procurement-button">{t('tracking.retry')}</button>}
        </>}
      </div>
      <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 px-4 py-4 sm:px-6">
        {manual && preview && !preview.tracked && <button type="button" disabled={saving} onClick={() => { request.current?.abort(); target.current = null; setPreview(null); setError(''); telegram.reset(); }} className="procurement-button sm:mr-auto">{t('tracking.change')}</button>}
        <button type="button" disabled={saving} onClick={onClose} className="procurement-button">{t(preview?.tracked ? 'tracking.close' : 'tracking.cancel')}</button>
        {preview?.tracked ? <Link to="/tracker" onClick={onClose} className="procurement-button procurement-button-primary">{t('tracking.openTracker')}</Link> : preview && <button type="button" disabled={saving || loading || !preview.telegram_linked || preview.balance < preview.cost} onClick={confirm} className="procurement-button procurement-button-primary">
          {saving && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}{t(saving ? 'tracking.saving' : 'tracking.confirm')}
        </button>}
      </footer>
    </div>
  </div>, document.body);
}
